import type { SelectedSlot } from "../bookingFlowMachine"
import { ScreeningProgram, useAvailability, type AvailabilitySlot } from "../hooks"
import { Alert, BackIcon, Box, ForwardIcon, Progress, Typography, dayjs } from "@carrotfertility/carotene-core"
import { FormButton, useFormContext, useWatch } from "@carrotfertility/carotene-core-x"
import React from "react"
import { useIntl } from "react-intl"

// 6 days per page (matching the design), which also divides the 30-day horizon evenly.
const DAYS_PER_PAGE = 6
// Members can only book within the next 30 days (enforced authoritatively by the BFF); the
// date strip won't page past this horizon.
const HORIZON_DAYS = 30

// The subset of the preferences form this section reads/writes. `selectedSlot` matches the
// machine's SelectedSlot so it can be handed straight to CONFIRM_BOOKING.
type AvailabilityFormValues = {
  selectedDate: string | null
  selectedSlot: SelectedSlot | null
}

type AppointmentAvailabilityProps = {
  program: ScreeningProgram
  // The submitted visit type + location of care that key the availability query. This
  // component only renders once the member has submitted those (see SelectPreferencesPage),
  // so both are always present.
  appointmentTypeId: string
  stateCode: string
  // State of the booking call the parent fires on submit — drives the CTA's pending/disabled
  // state and the inline error on failure.
  booking: { isPending: boolean; isError: boolean }
}

// The date strip + "Available times" grid, revealed after the member submits their visit
// preferences (MEM-716). Availability comes back as UTC slots; everything here is rendered in
// the member's local timezone (dayjs parses the ISO string and formats to local).
export function AppointmentAvailability({
  program,
  appointmentTypeId,
  stateCode,
  booking
}: AppointmentAvailabilityProps): JSX.Element {
  const intl = useIntl()
  const { setValue } = useFormContext<AvailabilityFormValues>()
  const selectedDate = useWatch<AvailabilityFormValues, "selectedDate">({ name: "selectedDate" })
  const selectedSlot = useWatch<AvailabilityFormValues, "selectedSlot">({ name: "selectedSlot" })

  // Which page of dates the strip is showing; 0 is the page starting today.
  const [weekOffset, setWeekOffset] = React.useState(0)

  // Work in UTC calendar dates: getAvailability treats from/to as UTC days, and slot start
  // times are UTC ISO, so the query window, the date strip, and slot grouping all share one
  // basis. (Slot *times* are still formatted to the member's local zone below.)
  const today = dayjs(new Date().toISOString().slice(0, 10))
  const horizonEnd = today.add(HORIZON_DAYS, "day") // exclusive

  // Query the whole bookable horizon [today, today + 30 days) at once. This tells us whether
  // ANY dates are available (for the empty state) and lets us page through them client-side.
  const availabilityQuery = useAvailability({
    program,
    appointmentTypeId,
    stateCode,
    language: intl.locale,
    from: today.format("YYYY-MM-DD"),
    to: horizonEnd.format("YYYY-MM-DD")
  })

  // The visible page of dates (display only — the query already spans the full horizon). Drop
  // any day at/after the horizon so the strip never shows a date more than 30 days out.
  const pageStart = today.add(weekOffset * DAYS_PER_PAGE, "day")
  const days = Array.from({ length: DAYS_PER_PAGE }, (_, index) => pageStart.add(index, "day")).filter((day) =>
    day.isBefore(horizonEnd)
  )
  const canGoNext = pageStart.add(DAYS_PER_PAGE, "day").isBefore(horizonEnd)

  // Slots that fall on the selected day. startTime is UTC ISO, so its first 10 chars are the
  // UTC date — compared against the (also-UTC) selectedDate.
  const slotsForSelectedDate = React.useMemo(() => {
    if (!selectedDate || !availabilityQuery.data) return []
    return availabilityQuery.data.filter((slot) => slot.startTime.slice(0, 10) === selectedDate)
  }, [selectedDate, availabilityQuery.data])

  const chooseDate = (date: string) => {
    setValue("selectedDate", date)
    // A new day invalidates any previously chosen time.
    setValue("selectedSlot", null)
  }

  const chooseSlot = (slot: AvailabilitySlot) => {
    setValue("selectedSlot", {
      slotToken: slot.slotToken,
      startTime: slot.startTime,
      offeringId: slot.offeringId,
      availableHostIds: slot.availableHostIds
    })
  }

  const goToPage = (offset: number) => {
    setWeekOffset(offset)
    // The visible dates shift out from under the selection; clear it.
    setValue("selectedDate", null)
    setValue("selectedSlot", null)
  }

  if (availabilityQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Progress />
      </Box>
    )
  }
  if (availabilityQuery.isError) {
    return <Alert severity="error">Something went wrong loading appointment times. Please try again.</Alert>
  }
  // No slots anywhere in the next 30 days — show the empty state instead of the date strip.
  // Nothing to book, so the CTA is a disabled "Continue" (matching the design).
  if (!availabilityQuery.data || availabilityQuery.data.length === 0) {
    return (
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}>
        <Alert severity="info">No dates available within the next 30 days.</Alert>
        <Box>
          <FormButton type="submit" color="secondary" disabled>
            Continue
          </FormButton>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.md) })}>
      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}>
        <Typography variant="h5" component="h2">
          Select a date
        </Typography>
        <Box sx={(theme) => ({ display: "flex", alignItems: "stretch", gap: theme.spacing(theme.tokens.spacing.xs) })}>
          {weekOffset > 0 && (
            <StripChevron label="Previous dates" icon={<BackIcon />} onClick={() => goToPage(weekOffset - 1)} />
          )}
          {days.map((day) => {
            const date = day.format("YYYY-MM-DD")
            return <DateCard key={date} day={day} selected={selectedDate === date} onSelect={() => chooseDate(date)} />
          })}
          {canGoNext && (
            <StripChevron label="Next dates" icon={<ForwardIcon />} onClick={() => goToPage(weekOffset + 1)} />
          )}
        </Box>
      </Box>

      {selectedDate && (
        <Box
          sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.sm) })}
        >
          <Typography variant="h5" component="h2">
            Available times
          </Typography>
          {slotsForSelectedDate.length === 0 ? (
            <Typography color="text.secondary">
              No appointment times are available for this date. Try another date or location.
            </Typography>
          ) : (
            <Box
              sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                gap: theme.spacing(theme.tokens.spacing.sm),
                maxInlineSize: 560
              })}
            >
              {slotsForSelectedDate.map((slot) => (
                <TimeSlotButton
                  key={slot.slotToken}
                  label={dayjs(slot.startTime).format("h:mm A")}
                  selected={selectedSlot?.slotToken === slot.slotToken}
                  onSelect={() => chooseSlot(slot)}
                />
              ))}
            </Box>
          )}
        </Box>
      )}

      {/* Policy content/route is out of scope for MEM-716 — placeholder link only. */}
      <Typography component="div" color="text.secondary">
        <Box
          component="button"
          type="button"
          sx={(theme) => ({
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            color: theme.palette.text.secondary,
            textDecoration: "underline",
            textUnderlineOffset: "3px"
          })}
        >
          Review our cancellation policy.
        </Box>
      </Typography>

      {booking.isError && (
        <Alert severity="error">Something went wrong booking your appointment. Please try again.</Alert>
      )}

      <Box>
        <FormButton type="submit" color="secondary" disabled={!selectedSlot || booking.isPending}>
          {booking.isPending ? "Booking…" : "Confirm booking"}
        </FormButton>
      </Box>
    </Box>
  )
}

type DateCardProps = { day: dayjs.Dayjs; selected: boolean; onSelect: () => void }

// One day in the date strip: a tall card of weekday / day-of-month / month, as a selectable
// button. Cards share the row width equally (flex: 1), matching the design.
function DateCard({ day, selected, onSelect }: DateCardProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      aria-label={day.format("dddd, MMMM D")}
      sx={(theme) => ({
        flex: "1 1 0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(theme.tokens.spacing.xs),
        minInlineSize: 56,
        minBlockSize: 104,
        paddingBlock: theme.spacing(theme.tokens.spacing.md),
        paddingInline: theme.spacing(theme.tokens.spacing.xs),
        cursor: "pointer",
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.md} solid ${
          // secondary is a workaround because border.light was too dark.
          selected ? theme.palette.secondary.dark : theme.palette.secondary.light
        }`,
        borderRadius: theme.tokens.borderRadius.md
      })}
    >
      <Typography variant="body2" component="span" color="text.secondary">
        {day.format("ddd")}
      </Typography>
      <Typography variant="h4" component="span">
        {day.format("D")}
      </Typography>
      <Typography variant="body2" component="span" color="text.secondary">
        {day.format("MMM")}
      </Typography>
    </Box>
  )
}

type StripChevronProps = { label: string; icon: JSX.Element; onClick: () => void }

// The date-strip pager control, styled as a card-height bordered box to match the design.
function StripChevron({ label, icon, onClick }: StripChevronProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      sx={(theme) => ({
        flex: "0 0 auto",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        inlineSize: 48,
        minBlockSize: 104,
        cursor: "pointer",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.md} solid ${theme.palette.secondary.dark}`,
        borderRadius: theme.tokens.borderRadius.md
      })}
    >
      {icon}
    </Box>
  )
}

type TimeSlotButtonProps = { label: string; selected: boolean; onSelect: () => void }

// One appointment time, as a selectable button.
function TimeSlotButton({ label, selected, onSelect }: TimeSlotButtonProps): JSX.Element {
  return (
    <Box
      component="button"
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      sx={(theme) => ({
        paddingBlock: theme.spacing(theme.tokens.spacing.sm),
        paddingInline: theme.spacing(theme.tokens.spacing.md),
        cursor: "pointer",
        backgroundColor: theme.palette.background.paper,
        border: `${theme.tokens.borderWidth.md} solid ${
          // secondary is a workaround because border.light was too dark.
          selected ? theme.palette.secondary.dark : theme.palette.secondary.light
        }`,
        borderRadius: theme.tokens.borderRadius.md,
        font: "inherit",
        color: theme.palette.text.primary
      })}
    >
      {label}
    </Box>
  )
}
